// @ts-nocheck

import React, { memo, forwardRef, useEffect, useMemo, createRef } from 'react';
import { Stage, Layer, Group, Rect } from 'react-konva';
import { ReactReduxContext, Provider } from 'react-redux';
import styled from 'styled-components';

import { Target } from '../../../client';
import BonusContainer from '../containers/BonusContainer';
import HeaderContainer from '../containers/HeaderContainer';
import MainAreaContainer from '../containers/MainAreaContainer';
import OutcomeContainer from '../containers/OutcomeContainer';
import SystemHeaderContainer from '../containers/SystemHeaderContainer';
import BetshareContext from '../contexts/BetshareContext';

import Footer from './Footer';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  margin: ${Target.__MOBILE__ ? '1em 1em 5em' : '0 0 5em'};
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
  transition: opacity 300ms ease-out;
  box-shadow: 1px 0 11px 1px #000;

  canvas,
  .konvajs-content {
    width: 100% !important;
    height: auto !important;
  }

  canvas {
    position: relative !important;
  }
`;

const CANVAS_WIDTH = 400;

const Canvas = memo(
  forwardRef((props, ref) => {
    const {
      betslipReference,
      canvasAreas,
      outcomesDetails,
      images,
      showWinningsAndStake,
      nickname,
      showPseudo,
      isReadyToShare,
      setIsReadyToShare,
    } = props;

    const [stageHeight, setStageHeight] = React.useState(null);
    const [heights, setHeights] = React.useState({});

    const isSystem = useMemo(() => canvasAreas.indexOf('systemHeader') !== -1, [canvasAreas]);
    const hasBonus = useMemo(() => canvasAreas.indexOf('bonus') !== -1, [canvasAreas]);

    const allRefs = useMemo(
      () => canvasAreas.reduce((acc, cur) => ({ ...acc, [cur]: createRef() }), {}),
      [canvasAreas],
    );

    const nbHeights = useMemo(() => Object.keys(heights).length, [heights]);
    // Used to wait for height computation before drawing to avoid a flash with "moving elements"
    const isHeightsReady = useMemo(() => nbHeights === Object.keys(allRefs).length, [allRefs, nbHeights]);
    const stageWidth = CANVAS_WIDTH;

    useEffect(() => {
      if (stageHeight !== null) {
        setIsReadyToShare(true);
      }
    }, [setIsReadyToShare, stageHeight]);

    useEffect(() => {
      if (isHeightsReady) {
        const canvas = ref.current;
        const clientRect = canvas.getClientRect();
        setStageHeight(clientRect.height);
      }
    }, [heights, isHeightsReady, ref]);

    useEffect(
      () => {
        const tmpHeights = {};
        Object.keys(allRefs).forEach((id) => {
          if (allRefs[id].current) {
            const { height } = allRefs[id].current.getClientRect();
            if (!isNaN(height)) {
              tmpHeights[id] = Math.ceil(height, 10);
            }
          }
        });
        if (Object.keys(allRefs).length === Object.keys(tmpHeights).length) {
          setHeights(tmpHeights);
        }
      },
      [allRefs, nbHeights, showWinningsAndStake], // we watch showWinningsAndStake because we need to redraw canvas if betshare has a bonus (change the total height of canvas)
    );

    const {
      header: heightHeader,
      mainArea: heightMainArea,
      bonus: heightBonus,
      systemHeader: heightSystemHeader,
      footer: heightFooter,
      ...outcomesHeights
    } = heights;

    const selectionY = useMemo(
      () =>
        heightMainArea + // mainArea already has header's height included, no need to add it.
          (heightSystemHeader || 0) +
          (heightBonus || 0) || 0,
      [heightBonus, heightMainArea, heightSystemHeader],
    );

    const footerY = useMemo(
      () => selectionY + Object.keys(outcomesHeights).reduce((acc, cur) => acc + (outcomesHeights[cur] || 0), 0) || 0,
      [outcomesHeights, selectionY],
    );

    return (
      <Wrapper $hide={!isReadyToShare}>
        <ReactReduxContext.Consumer>
          {({ store }) => (
            <Stage ref={ref} width={stageWidth} height={stageHeight}>
              <Provider store={store}>
                <BetshareContext.Provider
                  value={{
                    images,
                    stageWidth,
                    userPrefs: { showWinningsAndStake, showPseudo, nickname },
                  }}
                >
                  <Layer>
                    <Rect x={0} y={0} width={stageWidth} height={footerY + heightFooter || 0} fill="#000" />
                    <HeaderContainer
                      forwardRef={allRefs.header}
                      betslipReference={betslipReference}
                      x={0}
                      y={0}
                      nickname={showPseudo ? nickname : null}
                    />
                    <MainAreaContainer
                      forwardRef={allRefs.mainArea}
                      betslipReference={betslipReference}
                      x={0}
                      y={heightHeader || 0}
                    />
                    {showWinningsAndStake && hasBonus ? (
                      <BonusContainer
                        forwardRef={allRefs.bonus}
                        betslipReference={betslipReference}
                        x={0}
                        y={heightMainArea || null}
                      />
                    ) : (
                      <Rect ref={allRefs.bonus} x={0} y={0} width={0} height={0} />
                    )}
                    {isSystem && (
                      <SystemHeaderContainer
                        forwardRef={allRefs.systemHeader}
                        betslipReference={betslipReference}
                        x={0}
                        y={(heightBonus || 0) + (heightMainArea || 0)}
                      />
                    )}
                    <Group x={0} y={selectionY}>
                      {outcomesDetails.map((detail, index) => {
                        const { outcomeId, odds } = detail;
                        const y = outcomesDetails
                          .slice(0, index)
                          .reduce((acc, cur) => acc + (heights[cur.outcomeId] || 0), 0);
                        const isLast = index === outcomesDetails.length - 1;
                        return (
                          <Group key={outcomeId} x={0} y={y}>
                            <Rect
                              x={0}
                              y={0}
                              width={stageWidth}
                              height={heights[outcomeId] || null}
                              fill={index % 2 !== 0 ? '#1B1A1E' : '#000'}
                            />
                            <OutcomeContainer
                              forwardRef={allRefs[outcomeId]}
                              betslipReference={betslipReference}
                              outcomeId={outcomeId}
                              x={0}
                              y={0}
                              odds={odds}
                              isLast={isLast}
                            />
                          </Group>
                        );
                      })}
                    </Group>
                    <Footer ref={allRefs.footer} x={0} y={footerY} />
                  </Layer>
                </BetshareContext.Provider>
              </Provider>
            </Stage>
          )}
        </ReactReduxContext.Consumer>
      </Wrapper>
    );
  }),
);

export default Canvas;
